const STORAGE_PREFIX = "chat-draft";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/** Draft slot for the composer when no session is selected (new chat). */
export const NEW_CHAT_DRAFT_KEY = "__new__";

type StoredDraft = {
    text: string;
    savedAt: number;
};

export function getChatDraftKey(sessionId: string | undefined): string {
    return sessionId ?? NEW_CHAT_DRAFT_KEY;
}

function storageKey(bagId: number, appName: string, draftKey: string): string {
    return `${STORAGE_PREFIX}:${bagId}:${appName}:${draftKey}`;
}

function isExpired(savedAt: number): boolean {
    return Date.now() - savedAt > DRAFT_TTL_MS;
}

function parseStoredValue(raw: string): StoredDraft | null {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (
            parsed &&
            typeof parsed === "object" &&
            "text" in parsed &&
            typeof (parsed as StoredDraft).text === "string" &&
            typeof (parsed as StoredDraft).savedAt === "number"
        ) {
            return {
                text: (parsed as StoredDraft).text,
                savedAt: (parsed as StoredDraft).savedAt,
            };
        }
    } catch {
        // ignore invalid JSON
    }
    return null;
}

export function readChatDraft(
    bagId: number,
    appName: string,
    draftKey: string,
): string {
    try {
        const key = storageKey(bagId, appName, draftKey);
        const raw = localStorage.getItem(key);
        if (!raw) {
            return "";
        }

        const draft = parseStoredValue(raw);
        if (!draft || isExpired(draft.savedAt)) {
            localStorage.removeItem(key);
            return "";
        }

        return draft.text;
    } catch {
        return "";
    }
}

export function writeChatDraft(
    bagId: number,
    appName: string,
    draftKey: string,
    text: string,
): void {
    try {
        const key = storageKey(bagId, appName, draftKey);
        if (!text) {
            localStorage.removeItem(key);
        } else {
            const payload: StoredDraft = {text, savedAt: Date.now()};
            localStorage.setItem(key, JSON.stringify(payload));
        }
    } catch {
        // ignore quota / private mode
    }
}

export function clearChatDraft(
    bagId: number,
    appName: string,
    draftKey: string,
): void {
    writeChatDraft(bagId, appName, draftKey, "");
}
